import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Breadcrumbs,
  Link,
  Alert,
} from '@mui/material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { subcategoriesService, categoriesService } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import IconUpload from '../components/IconUpload';

const SubcategoryEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Déterminer si nous sommes en mode création
  const isNewSubcategory = location.pathname === '/subcategories/new';
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [error, setError] = useState('');
  const [icon, setIcon] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    categoryId: '',
    displayOrder: 0,
  });

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Charger les catégories
      const categoriesData = await categoriesService.getAll();
      setCategories(categoriesData.data);
      
      if (!isNewSubcategory && id) {
        const subcategoryData = await subcategoriesService.getById(id);
        if (subcategoryData && subcategoryData.data) {
          setFormData({
            name: subcategoryData.data.name || '',
            description: subcategoryData.data.description || '',
            categoryId: subcategoryData.data.categoryId || '',
            displayOrder: subcategoryData.data.displayOrder || 0,
          });

          // Charger l'icône existante si disponi ble
          if (subcategoryData.data.icon) {
            setIcon({
              id: 'existing-icon',
              preview: `${'https://api.inklandtattoo.fr'}${subcategoryData.data.icon}`,
              name: 'Icône existante',
              file: null,
              isExisting: true
            });
          }


        }
      }
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
      setError('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError('');

      // Préparer les données avec l'icône
      const submitData = new FormData();
      submitData.append('name', formData.name);
      submitData.append('description', formData.description);
      submitData.append('categoryId', formData.categoryId);
      submitData.append('displayOrder', formData.displayOrder.toString());
      
      // Ajouter l'icône si elle existe et n'est pas déjà sauvegardée
      if (icon && icon.file && !icon.isExisting) {
        submitData.append('icon', icon.file);
      }

      let subcategoryId;
      if (isNewSubcategory) {
        const response = await subcategoriesService.create(submitData);
        subcategoryId = response.data.id;
      } else {
        await subcategoriesService.update(id, submitData);
        subcategoryId = id;
      }



      navigate('/subcategories');
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      setError('Erreur lors de la sauvegarde de la sous-catégorie');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  if (loading) {
    return <LoadingSpinner message="Chargement de la sous-catégorie..." />;
  }

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 3 }}>
        <Link
          color="inherit"
          href="/subcategories"
          onClick={(e) => {
            e.preventDefault();
            navigate('/subcategories');
          }}
        >
          Sous-catégories
        </Link>
        <Typography color="text.primary">
          {isNewSubcategory ? 'Nouvelle sous-catégorie' : 'Modifier la sous-catégorie'}
        </Typography>
      </Breadcrumbs>

      <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
        {isNewSubcategory ? 'Nouvelle sous-catégorie' : 'Modifier la sous-catégorie'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nom de la sous-catégorie"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required>
                <InputLabel>Catégorie parente</InputLabel>
                <Select
                  value={formData.categoryId}
                  onChange={(e) => handleChange('categoryId', e.target.value)}
                >
                  {categories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Ordre d'affichage"
                type="number"
                value={formData.displayOrder}
                onChange={(e) => handleChange('displayOrder', parseInt(e.target.value) || 0)}
                helperText="Un nombre plus petit sera affiché en premier"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={4}
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <IconUpload
                icon={icon}
                onIconChange={setIcon}
                title="Icône de la sous-catégorie"
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/subcategories')}
                  disabled={saving}
                >
                  Annuler
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={saving}
                >
                  {saving ? 'Sauvegarde...' : (isNewSubcategory ? 'Créer' : 'Modifier')}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};

export default SubcategoryEdit;
